import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'
import styled, { keyframes } from 'styled-components'

import { PreviewPageNavigator } from './PreviewPageNavigator'

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: ${(props) => props.theme.forecast.cardGroupBackground};
  border-radius: 0.375rem;
  overflow: hidden;
  position: relative;
`

const Skeleton = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.forecast.cardGroupBackground} 25%,
    ${({ theme }) => theme.forecast.hoverButton} 50%,
    ${({ theme }) => theme.forecast.cardGroupBackground} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`

const ImageWrapper = styled.div<{ $width: number; $height: number }>`
  position: relative;
  display: inline-block;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
`

const StyledImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
`

interface DocumentPreviewViewerProps {
  thumbnailUrl: string | null
  hiResUrl: string | null
  isThumbLoading: boolean
  alt: string
  documentId: string
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

export const DocumentPreviewViewer: React.FC<DocumentPreviewViewerProps> = ({
  thumbnailUrl,
  hiResUrl,
  isThumbLoading,
  alt,
  documentId,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [minScale, setMinScale] = useState(0.01)
  const [fitComplete, setFitComplete] = useState(false)
  const [imgNatural, setImgNatural] = useState<{
    w: number
    h: number
  } | null>(null)
  const [hiResReady, setHiResReady] = useState(false)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)

  const calculateAndApplyFit = useCallback(
    (natural: { w: number; h: number }, animate = false) => {
      const container = containerRef.current
      const transform = transformRef.current
      if (!container || !transform) return

      const rect = container.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return

      const scaleX = rect.width / natural.w
      const scaleY = rect.height / natural.h
      const fitScale = Math.min(scaleX, scaleY)

      setMinScale(fitScale)

      const posX = (rect.width - natural.w * fitScale) / 2
      const posY = (rect.height - natural.h * fitScale) / 2

      transform.setTransform(posX, posY, fitScale, animate ? 200 : 0)
    },
    []
  )

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = e.currentTarget
      const natural = { w: naturalWidth, h: naturalHeight }
      setImgNatural(natural)
      setInitialLoadComplete(true)
      setTimeout(() => {
        calculateAndApplyFit(natural)
        setTimeout(() => setFitComplete(true), 100)
      }, 0)
    },
    [calculateAndApplyFit]
  )

  useEffect(() => {
    setFitComplete(false)
    setImgNatural(null)
    setMinScale(0.01)
    setHiResReady(false)
    setInitialLoadComplete(false)
  }, [documentId])

  useEffect(() => {
    setFitComplete(false)
    setImgNatural(null)
    setMinScale(0.01)
    setHiResReady(false)
  }, [currentPage])

  useEffect(() => {
    if (!hiResUrl) return
    setHiResReady(false)
    const img = new Image()
    img.src = hiResUrl
    img.onload = () => setHiResReady(true)
    return () => {
      img.onload = null
    }
  }, [hiResUrl])

  useEffect(() => {
    if (!imgNatural) return
    const onResize = () => calculateAndApplyFit(imgNatural)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [imgNatural, calculateAndApplyFit])

  const showSkeleton = !initialLoadComplete && isThumbLoading
  const showViewer = !showSkeleton && !!thumbnailUrl

  return (
    <Container ref={containerRef}>
      {showSkeleton && <Skeleton />}
      {showViewer && (
        <TransformWrapper
          ref={transformRef}
          minScale={minScale}
          maxScale={10}
          limitToBounds
          centerOnInit={false}
          wheel={{ step: 0.5 }}
          doubleClick={{ disabled: false, mode: 'zoomIn' }}
          pinch={{ disabled: false }}
          alignmentAnimation={{ sizeX: 100, sizeY: 100 }}>
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
              cursor: 'grab',
            }}
            contentStyle={{
              display: 'inline-block',
              opacity: fitComplete ? 1 : 0,
            }}>
            {imgNatural ? (
              <ImageWrapper $width={imgNatural.w} $height={imgNatural.h}>
                <StyledImage
                  src={hiResReady && hiResUrl ? hiResUrl : thumbnailUrl}
                  alt={alt}
                  draggable={false}
                />
              </ImageWrapper>
            ) : null}
            {!imgNatural && (
              <img
                src={thumbnailUrl}
                alt=''
                onLoad={handleImageLoad}
                style={{
                  position: 'absolute',
                  opacity: 0,
                  pointerEvents: 'none',
                }}
              />
            )}
          </TransformComponent>
        </TransformWrapper>
      )}
      <PreviewPageNavigator
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
    </Container>
  )
}
